import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationListCursor,
  ConversationListItemResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  Message,
  MessageWithConversationId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { ConversationTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useCallback, useMemo } from "react";
import { fetchConversationsPage } from "../db/collections";
import { filterGroupToTanstackExpression } from "../filters/conversation-tanstack-expressions";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { DEFAULT_PAGE_SIZE } from "../table/types";
import { useLiveInfiniteCursorQuery } from "./use-live-infinite-cursor-query";

export interface UseConversationListResult {
  conversations: ConversationListItemResponse[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

export function useConversations(): {
  data: ConversationListItemResponse[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryOptions = useQueryOptions();

  const { filterGroup } = queryOptions;

  const fetchPage = useCallback(
    async (cursor: ConversationListCursor | null) => {
      const result = await fetchConversationsPage(effectApi, {
        limit: DEFAULT_PAGE_SIZE,
        cursor: cursor ?? undefined,
        filterGroup,
      });

      for (const item of result.items) {
        collections.conversationsList.utils.writeInsert(item);
      }

      return { items: result.items, nextCursor: result.nextCursor };
    },
    [effectApi, collections.conversationsList.utils, filterGroup],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLiveInfiniteCursorQuery(
      (q) => {
        const baseQuery = q
          .from({ conversation: collections.conversationsList })
          .orderBy(
            ({ conversation }) => conversation.lastCustomerMessageAt,
            "desc",
          );

        return baseQuery.where(({ conversation }) => {
          const expr = filterGroupToTanstackExpression(
            filterGroup,
            conversation,
          );
          return expr ?? eq(conversation.id, conversation.id);
        });
      },
      {
        pageSize: DEFAULT_PAGE_SIZE,
        getCursor: (lastItem) => {
          if (!lastItem) {
            return null;
          }
          const conversation = lastItem;
          if (!conversation.id) {
            return null;
          }
          return {
            lastCustomerMessageAt: conversation.lastCustomerMessageAt,
            id: conversation.id,
          };
        },
        fetchPage,
      },
      [queryOptions],
    );

  const conversations = useMemo(() => {
    return data.filter(
      (row): row is ConversationListItemResponse => row != null && "id" in row,
    );
  }, [data]);

  return {
    data: conversations,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: false,
  };
}

export function useConversation(conversationId: ConversationId): {
  data: ConversationListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ conversation: collections.conversationsList })
        .where(({ conversation }) => eq(conversation.id, conversationId))
        .findOne(),
    [conversationId],
  );

  return { data: result.data, isLoading: result.isLoading };
}

export function useConversationThreads(conversationId: ConversationId): {
  data: ConversationThread[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.conversationId, conversationId)),
    [conversationId],
  );

  return { data: result.data, isLoading: result.isLoading };
}

export function useConversationMessages(conversationId: ConversationId): {
  data: MessageWithConversationId[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId)),
    [conversationId],
  );

  const data = (result.data ?? []).filter(
    (message): message is MessageWithConversationId => message !== undefined,
  );

  return { data, isLoading: result.isLoading };
}

export function useThreadMessages(
  conversationId: ConversationId,
  threadId: string,
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [conversationId],
  );

  const data = useMemo(
    () => result.data.filter((m) => m.conversationThreadId === threadId),
    [result.data, threadId],
  );

  return { data, isLoading: result.isLoading };
}

export function useConversationTimelineEvents(conversationId: ConversationId): {
  data: ConversationTimelineEventResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ event: collections.timelineEvents })
        .where(({ event }) => eq(event.conversationId, conversationId))
        .orderBy(({ event }) => event.occurredAt, "desc"),
    [conversationId],
  );

  const data = (result.data ?? []).filter(
    (event): event is ConversationTimelineEventResponse => event !== undefined,
  );

  return { data, isLoading: result.isLoading };
}

export function useSingleConversationData(conversationId: ConversationId): {
  conversation: ConversationListItemResponse | undefined;
  threads: ConversationThread[];
  messages: Message[];
  isLoading: boolean;
} {
  const conversationResult = useConversation(conversationId);
  const threadsResult = useConversationThreads(conversationId);
  const messagesResult = useConversationMessages(conversationId);

  return {
    conversation: conversationResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading:
      threadsResult.isLoading ||
      messagesResult.isLoading ||
      conversationResult.isLoading,
  };
}
